const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export class ApiService {
  private token: string | null = null;

  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('aix_token', token);
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('aix_token');
    }
    return this.token;
  }

  clearToken(): void {
    this.token = null;
    localStorage.removeItem('aix_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>)
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  }

  // Auth endpoints
  async register(email: string, password: string, name: string) {
    return this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name })
    });
  }

  async login(email: string, password: string) {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
  }

  async getProfile() {
    return this.request('/api/auth/me');
  }

  // Gesture endpoints
  async getGestures() {
    return this.request('/api/gestures');
  }

  async createGesture(data: any) {
    return this.request('/api/gestures', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async deleteGesture(id: string) {
    return this.request(`/api/gestures/${id}`, {
      method: 'DELETE'
    });
  }

  // AI endpoints
  async getAIContext(text: string, history: string[]) {
    return this.request('/api/ai/context', {
      method: 'POST',
      body: JSON.stringify({ text, history })
    });
  }

  async completeSentence(text: string, context: string[]) {
    return this.request('/api/ai/complete', {
      method: 'POST',
      body: JSON.stringify({ text, context })
    });
  }

  async translate(text: string, targetLang: string, sourceLang?: string) {
    return this.request('/api/ai/translate', {
      method: 'POST',
      body: JSON.stringify({ text, targetLang, sourceLang })
    });
  }

  async getSupportedLanguages() {
    return this.request('/api/ai/languages');
  }

  // Analytics endpoints
  async trackEvent(eventType: string, eventData: any) {
    return this.request('/api/analytics/track', {
      method: 'POST',
      body: JSON.stringify({ eventType, eventData })
    });
  }

  async getStats() {
    return this.request('/api/analytics/stats');
  }

  async getReport(startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/api/analytics/report?${params.toString()}`);
  }
}

export const apiService = new ApiService();
